import { FC, useEffect, useState } from "react";
import styled from "@emotion/styled";
import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonVariant,
  MDSDialogSlotName,
  MDSModalDialog,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
} from "@/mds";

interface EditPromptModalProps {
  isOpen: boolean;
  scopeLabel: string;
  initialValue: string;
  onClose: () => void;
  onSave: (next: string) => Promise<void> | void;
}

export const EditPromptModal: FC<EditPromptModalProps> = ({
  isOpen,
  scopeLabel,
  initialValue,
  onClose,
  onSave,
}) => {
  const [draft, setDraft] = useState(initialValue);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDraft(initialValue);
      setSaving(false);
    }
  }, [isOpen, initialValue]);

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await onSave(draft);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <StyledDialog isOpen={isOpen} handleClose={onClose}>
      <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
        Edit prompt
      </MDSText>
      <Body slot={MDSDialogSlotName.Content}>
        <MDSText variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Tertiary}>
          Instructions Mem follows when composing or maintaining the {scopeLabel}.
        </MDSText>
        <Textarea
          value={draft}
          autoFocus
          onChange={e => setDraft(e.target.value)}
          placeholder="Describe how the agent should approach this..."
        />
      </Body>
      <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
        <MDSButton onClick={onClose}>Cancel</MDSButton>
        <MDSButton
          variant={MDSButtonVariant.Contrast}
          isDisabled={saving || draft === initialValue}
          onClick={submit}
        >
          Save
        </MDSButton>
      </MDSButtonGroup>
    </StyledDialog>
  );
};

const StyledDialog = styled(MDSModalDialog)`
  .mds-dialog {
    padding: ${({ theme }) => theme.spacing[7]};
    width: min(640px, 90vw);
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["3"]};
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 240px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: ${({ theme }) => theme.spacing["3"]};
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
  line-height: 1.5;
  resize: vertical;
  outline: none;
  background: ${({ theme }) => theme.colors.surface.pane};
  color: inherit;
  &:focus {
    border-color: ${({ theme }) => theme.colors.text.primary};
  }
`;
